import clsx from 'clsx'
import NextImage, { type ImageProps as NextImageProps } from 'next/image'
import { type CSSProperties } from 'react'
import { Box, type BoxProps } from '../Box'
import { defaultImageStyle } from './Image.css'

type ContainerProps = Pick<
  BoxProps,
  | 'borderRadius'
  | 'flexShrink'
  | 'marginRight'
  | 'marginLeft'
  | 'marginTop'
  | 'marginBottom'
  | 'style'
  | 'position'
  | 'className'
>
type Props = Omit<
  NextImageProps,
  'className' | 'objectFit' | 'objectPosition'
> &
  ContainerProps & {
    // These are deprecated in NextImage and meant to be passed as style. Our custom wrapper handles this
    objectFit?: CSSProperties['objectFit']
    objectPosition?: CSSProperties['objectPosition']
    imageClassName?: string
  }

/** @deprecated */
export type { Props as ImageProps }

/*
 * For small images, default quality to a bit higher than normally. NextImage by default uses 75, but
 * lets try defaulting to 80 for a bit better quality. Images are hopefully already optimized anyway.
 */
const SMALL_IMAGE_CONSTRAIN = 120
const calcQuality = ({ width, height }: Props) =>
  (width ? Number(width) : SMALL_IMAGE_CONSTRAIN + 1) <=
    SMALL_IMAGE_CONSTRAIN &&
  (height ? Number(height) : SMALL_IMAGE_CONSTRAIN + 1) <= SMALL_IMAGE_CONSTRAIN
    ? 90
    : 80

/** @deprecated */
export const Image = ({
  borderRadius,
  className,
  flexShrink,
  marginRight,
  marginLeft,
  marginTop,
  marginBottom,
  objectFit = 'cover',
  objectPosition = 'center',
  style,
  children,
  position,
  quality,
  imageClassName,
  ...props
}: Props) => {
  const styleProps = style ?? {}
  const isRoundedImage = borderRadius && borderRadius !== 'none'

  if (isRoundedImage) {
    // Safari cant use border-radius+overflow without isolating the layer
    Object.assign(styleProps, { isolation: 'isolate' })
  }

  return (
    <Box
      {...{
        borderRadius,
        className,
        flexShrink,
        marginRight,
        marginLeft,
        marginTop,
        marginBottom,
        style,
        position,
      }}
      display="flex"
      style={styleProps}
      overflow={isRoundedImage ? 'hidden' : undefined}
    >
      <NextImage
        style={{ objectFit, objectPosition }}
        className={clsx(defaultImageStyle, imageClassName)}
        quality={quality ?? calcQuality(props)}
        {...props}
      />
      {children}
    </Box>
  )
}
